// src/pages/AckDetails.js
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { fmtDateTimeUS } from "../utils/dates";
import HeaderLogged from "../components/HeaderLogged";
import Footer from "../components/Footer";
import BUSINESS_RULES from "../data/business_rules.json";
import { explainAck } from "../utils/ackExplain";

async function fetchAckRules(token) {
  const res = await fetch(`/api/irs/ack-rules/public/`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`AckRules request failed: ${res.status} ${text}`);
  }
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

// Admin-curated AckRule note: first rule whose key is a substring of the reason.
function adminNoteFor(reason, rules) {
  const low = String(reason || "").toLowerCase();
  for (const r of rules || []) {
    const k = String(r?.key || "").toLowerCase();
    if (k && r?.message && low.includes(k)) return r.message;
  }
  return null;
}

/* ====== КОМПОНЕНТ СТРАНИЦЫ ====== */

function AckDetails() {
  const { id: companyId, submissionId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rules, setRules] = useState([]);
  const [explained, setExplained] = useState({ status: null, errors: [] });
  const [receivedAt, setReceivedAt] = useState(null);
  // ACK ещё не выпущен IRS — не ошибка, просто ждём (сервер добирает его сам)
  const [pending, setPending] = useState(null);

  const fetchAck = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");

      const [loadedRules, ackRes] = await Promise.all([
        fetchAckRules(token).catch(() => []), // не роняем страницу, если правила недоступны
        fetch(`/api/irs/ack/${submissionId}/`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      setRules(loadedRules || []);

      let json = null;
      try {
        json = await ackRes.json();
      } catch {
        json = null;
      }

      // 202 — IRS ещё не выпустил подтверждение; сервер продолжает добирать его сам
      if (ackRes.status === 202) {
        setPending(json || { detail: "Acknowledgment is not available yet." });
        setExplained({ status: null, errors: [] });
        setReceivedAt(null);
        return;
      }

      if (!ackRes.ok) {
        throw new Error(
          (json && (json.detail || json.error)) || `ACK request failed: ${ackRes.status}`
        );
      }

      setPending(null);
      setExplained(explainAck(json || {}, BUSINESS_RULES));
      // время получения берём с сервера: ACK сохранён один раз и отдаётся из базы
      setReceivedAt((json && json.fetched_at) || new Date().toISOString());
    } catch (e) {
      setError(e.message || "Unknown error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissionId]);

  const isRejected = explained.status === "Rejected";
  const isAccepted = explained.status === "Accepted";

  return (
    <div className="flex flex-col min-h-screen bg-[#E4F2FF]">
      <HeaderLogged />

      <main className="flex-1 p-4">
        <div className="max-w-3xl mx-auto bg-white shadow-lg">
          <div className="px-6 py-4 border-b bg-gray-50 flex items-center justify-between">
            <h1 className="text-xl sm:text-2xl font-semibold text-[#0F2C5E]">
              Acknowledgment Details
            </h1>
            <div className="flex gap-2">
              <button
                onClick={() => navigate(-1)}
                className="text-sm px-3 py-1 bg-gray-200 hover:bg-gray-300"
              >
                Back
              </button>
              <button
                onClick={() => navigate(`/company/${companyId}`)}
                className="text-sm px-3 py-1 bg-[#0F2C5E] text-white hover:bg-[#0F2C5E]/80"
              >
                Company
              </button>
            </div>
          </div>

          <div className="px-6 py-4 space-y-4">
            {loading ? (
              <div className="text-gray-600">Loading acknowledgment…</div>
            ) : error ? (
              <div className="text-red-600">
                Failed to fetch acknowledgment: {error}
              </div>
            ) : pending ? (
              <div className="p-4 border rounded bg-blue-50 border-blue-200">
                <div className="text-lg font-semibold text-[#0F2C5E]">
                  Waiting for the IRS acknowledgment
                </div>
                <p className="text-gray-700 mt-1">{pending.detail}</p>
                <p className="text-xs text-gray-500 mt-2">
                  There is nothing to do — we check with the IRS automatically and this
                  page will show the acknowledgment as soon as it is available.
                </p>
              </div>
            ) : (
              <>
                {/* Статус от IRS */}
                <div
                  className={`p-4 border rounded ${
                    isRejected
                      ? "bg-red-50 border-red-200"
                      : isAccepted
                        ? "bg-green-50 border-green-200"
                        : "bg-gray-50"
                  }`}
                >
                  <div
                    className={`text-lg font-semibold ${
                      isRejected
                        ? "text-red-800"
                        : isAccepted
                          ? "text-green-800"
                          : "text-gray-800"
                    }`}
                  >
                    {isRejected
                      ? "Rejected by the IRS"
                      : isAccepted
                        ? "Accepted by the IRS"
                        : "Acknowledgment received"}
                  </div>
                  <div
                    className={
                      isRejected
                        ? "text-red-800/90"
                        : isAccepted
                          ? "text-green-800/90"
                          : "text-gray-700"
                    }
                  >
                    {isRejected
                      ? "The IRS did not accept this submission. See the reasons and next steps below."
                      : isAccepted
                        ? "This submission was accepted. No action is needed."
                        : "Acknowledgment has been fetched from the IRS service."}
                  </div>
                  {receivedAt && (
                    <div className="text-xs opacity-70 mt-1">
                      Received at: {fmtDateTimeUS(receivedAt)}
                    </div>
                  )}
                </div>

                {/* Причины + что делать */}
                {explained.errors.length > 0 ? (
                  <div className="space-y-3">
                    {explained.errors.map((e, idx) => {
                      const note = adminNoteFor(e.reason, rules);
                      return (
                        <div key={idx} className="border rounded overflow-hidden">
                          <div className="px-4 py-2 bg-red-50 border-b border-red-100 font-semibold text-red-800">
                            {e.title}
                          </div>
                          <div className="px-4 py-3 space-y-2 text-sm">
                            {/* Сырой ответ IRS (код + текст правила) намеренно не показываем —
                                пользователь видит только понятное объяснение и что делать. */}
                            {note && (
                              <div className="text-gray-600">
                                <span className="font-medium">Note: </span>
                                {note}
                              </div>
                            )}
                            <div className="bg-[#eef4ff] border-l-4 border-[#0F2C5E] px-3 py-2 text-gray-800">
                              <span className="font-medium">What to do: </span>
                              {e.guidance}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="p-4 border rounded text-sm text-gray-600">
                    {isRejected
                      ? "The IRS did not provide error details for this submission. Please contact support@2290pay.org."
                      : "No validation errors found."}
                  </div>
                )}

                <div className="flex gap-2">
                  <button
                    onClick={fetchAck}
                    disabled={loading}
                    className="text-sm px-3 py-1 bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? "Checking…" : "Refresh"}
                  </button>
                  <button
                    onClick={() => navigate(-1)}
                    className="text-sm px-3 py-1 bg-gray-200 hover:bg-gray-300"
                  >
                    Close
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default AckDetails;
